import { useState } from 'react';
import { FileText, PackageCheck, Wallet, Truck, ArrowRight } from 'lucide-react';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import HeroCarousel, { CAROUSEL_IMAGES } from '@/components/HeroCarousel';
import { inr } from '@/lib/api';

const HOW = [
    { icon: <FileText />, title: 'Share drawing / cutlist', desc: 'Send us your 2D/3D drawing or cutlist.' },
    { icon: <PackageCheck />, title: 'We share board count', desc: 'Kanbr shares the exact number of boards required.' },
    { icon: <Wallet />, title: 'Approve and pay advance', desc: 'Approve the quote and pay advance to lock production.' },
    { icon: <Truck />, title: 'We manufacture & dispatch', desc: 'Sourcing, manufacturing and dispatch handled end-to-end.' },
];

const CARCASS = [
    { key: 'eco', label: 'Eco', material: 'Action Tesa HDHMR Prelam Board', price: 5499, blurb: 'Pre-pasted HDHMR — quick, clean and cost-efficient.' },
    { key: 'prime', label: 'Prime', material: 'Action Tesa HDHMR + Liner Laminate (Postlam)', price: 7499, blurb: 'Choose your liner laminate for a fully-customised interior.', highlight: true },
    { key: 'gold', label: 'Gold', material: 'Commercial Ply + Liner Laminate (Postlam)', price: 8499, blurb: 'Highest strength & moisture resistance for long-life builds.' },
];

const SHUTTERS = [
    { key: 'laminate', label: 'Laminate Shutter', price: 9999, unit: 'board' },
    { key: 'laminate_combo', label: 'Laminate Combination Shutter', price: 11999, unit: 'board' },
    { key: 'acrylic', label: 'Acrylic Shutter', price: 14999, unit: 'board' },
    { key: 'acrylic_combo', label: 'Acrylic Combination Shutter', price: 16999, unit: 'board' },
    { key: 'glass', label: 'Glass Shutter', price: 650, unit: 'sqft' },
];

export default function StandardPackage() {
    const [carcass, setCarcass] = useState('prime');
    const [shutter, setShutter] = useState('laminate');
    const [leadOpen, setLeadOpen] = useState(false);
    const c = CARCASS.find((x) => x.key === carcass);
    const sh = SHUTTERS.find((x) => x.key === shutter);
    const summaryMsg = `Hi Kanbr, I want the Standard Package. Carcass: ${c.label} — ${c.material} at ₹${c.price}/board. Shutter: ${sh.label} at ₹${sh.price}/${sh.unit}. Please guide me next steps.`;

    return (
        <div>
            <section className="bg-white border-b border-slate-200">
                <div className="kanbr-container py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-7">
                        <div className="eyebrow text-slate-500 mb-2">Offering 02</div>
                        <h1 className="font-display text-2xl md:text-3xl font-bold text-amber-600 leading-tight tracking-tight">Standard Package</h1>
                        <div className="font-display text-lg md:text-xl font-semibold text-slate-900 mt-2">We Source. We Manufacture.</div>
                        <p className="text-slate-600 mt-3 text-sm md:text-base max-w-xl leading-relaxed">Turnkey modular kitchen execution with material sourcing. Premium modular kitchen with materials and end-to-end wardrobe interior designer coordination — Eco, Prime or Gold build quality.</p>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <HeroCarousel images={CAROUSEL_IMAGES} interval={3000} alt="Kanbr standard package" />
                    </div>
                </div>
            </section>

            <section className="kanbr-container py-10">
                <div className="eyebrow mb-2">How it works</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-6">From drawing to installation.</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {HOW.map((st, i) => (
                        <div key={i} className="border border-slate-200 bg-white p-4 relative">
                            <div className="text-[10px] font-display font-bold text-amber-600 absolute top-3 right-4 text-lg">0{i + 1}</div>
                            <div className="w-10 h-10 bg-blue-900 text-white flex items-center justify-center mb-3">{st.icon}</div>
                            <h3 className="font-display font-semibold text-slate-900 text-sm">{st.title}</h3>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{st.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-white border-t border-slate-200">
                <div className="kanbr-container py-10">
                    <div className="eyebrow mb-2">Step 1 · Select carcass type</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-4">Pick your build quality.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {CARCASS.map((cc) => (
                            <button
                                key={cc.key}
                                onClick={() => setCarcass(cc.key)}
                                data-testid={`carcass-${cc.key}`}
                                className={`text-left border p-5 transition-all ${carcass === cc.key ? 'border-blue-900 ring-2 ring-blue-900 bg-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}
                            >
                                {cc.highlight && <div className="text-[9px] uppercase tracking-widest text-amber-600 mb-1 font-semibold">Popular</div>}
                                <div className="eyebrow mb-1">{cc.label}</div>
                                <div className="font-display text-3xl font-bold text-slate-900">{inr(cc.price)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">per board · + GST + Transport</div>
                                <div className="mt-3 text-sm font-medium text-slate-800">{cc.material}</div>
                                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{cc.blurb}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="kanbr-container py-10">
                <div className="eyebrow mb-2">Step 2 · Select shutter type</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-4">Choose your shutter finish.</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {SHUTTERS.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setShutter(s.key)}
                            data-testid={`shutter-${s.key}`}
                            className={`text-left border p-4 transition-all ${shutter === s.key ? 'border-blue-900 ring-2 ring-blue-900 bg-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}
                        >
                            <div className="font-display font-semibold text-slate-900 text-sm">{s.label}</div>
                            <div className="font-display text-xl font-bold text-blue-900 mt-2">{inr(s.price)}<span className="text-xs font-medium text-slate-500"> / {s.unit}</span></div>
                        </button>
                    ))}
                </div>

                <div className="mt-6 p-4 border-l-4 border-amber-600 bg-amber-50 text-xs text-slate-700">
                    <strong className="text-slate-900">Note:</strong> Above prices do not include hardware, installation, transport or GST.
                </div>

                <div className="mt-6">
                    <button onClick={() => setLeadOpen(true)} className="btn-primary rounded-none" data-testid="standard-proceed">
                        Proceed with selection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            <LeadCaptureModal
                open={leadOpen}
                onOpenChange={setLeadOpen}
                source="standard_package"
                defaultMessage={summaryMsg}
                extra={{ carcass, shutter }}
            />
        </div>
    );
}
